import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { IconSparkles, IconUsers, IconTicket, IconGift, IconScale } from '@tabler/icons-react';
import {
  setDocumentMeta, setCanonical, setOgImage, injectJsonLdList,
  buildWebPageSchema, buildFaqPageSchema, buildBreadcrumbSchema,
} from '@/lib/seo';
import { FaqAccordion } from '@/components/marketing/faq-accordion';
import { MarketingShell } from '@/components/marketing/warm/marketing-shell';
import { PageHero } from '@/components/marketing/warm/page-hero';
import { CtaBand } from '@/components/marketing/warm/cta-band';
import { SeatsVsTicketsCalculator } from '@/components/marketing/warm/seats-vs-tickets';
import { BAND, Eyebrow, H2, IconTile, Muted, RevealSection, useReveal } from '@/components/marketing/warm/primitives';

const FAQS = [
  {
    q: 'How does the helpdesk cost calculator work?',
    a: 'You enter your number of agents, the per-agent monthly price of the helpdesk you are comparing, and how many tickets your team handles per month. Per-seat cost is agents × price. Kora cost is tickets × $0.10 (1 credit per ticket, $1 = 10 credits). Nothing is sent anywhere; the math runs in your browser.',
  },
  {
    q: 'What does Kora charge per ticket?',
    a: 'One credit per ticket created, which is $0.10, on any channel. Resolving or closing a ticket is never charged. Members, comments, attachments, SLA tracking and automation rules are free, so adding an agent never changes the bill.',
  },
  {
    q: 'When is per-seat pricing cheaper than pay-per-ticket?',
    a: 'When each agent handles a lot of tickets. At $0.10 per ticket, a seat priced at $50/month breaks even at 500 tickets per agent per month. Above that, a flat per-seat plan costs less than usage pricing; below it, pay-per-ticket costs less. The calculator shows your own break-even point.',
  },
  {
    q: 'Does the calculator include AI answers?',
    a: 'Optionally. An AI Analyst answer costs 1 credit ($0.10) on success only. Add your expected monthly AI answers to include them in the Kora total. AI ticket structuring is free.',
  },
  {
    q: 'Which per-seat price should I enter?',
    a: 'Use the price on your current vendor invoice or the plan you are evaluating, including any AI or add-on fees you would pay per agent. Vendor prices change, so check the vendor pricing page for the current figure rather than relying on a number from a third party.',
  },
];

const MATH = [
  { icon: IconUsers, title: 'Per-seat', body: 'agents × price per agent per month. It does not change with ticket volume.' },
  { icon: IconTicket, title: 'Kora', body: 'one credit per ticket created ($0.10), one credit per successful AI Analyst answer. $1 buys 10 credits.' },
  { icon: IconGift, title: 'Free on Kora', body: 'members, comments, attachments, SLA tracking, automation rules, webhooks and CSV export. Adding an agent costs nothing.' },
  { icon: IconScale, title: 'Break-even tickets', body: '= (agents × per-agent price) ÷ $0.10.' },
];

function CalculatorBlock() {
  const ref = useReveal<HTMLDivElement>(150);
  return (
    <div ref={ref} className="home-reveal mt-12 space-y-6">
      <div className="rounded-2xl p-5 flex gap-4" style={{ background: 'var(--warm-card)', border: '1px solid var(--warm-border)' }}>
        <IconTile icon={IconSparkles} />
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: 'var(--warm-muted)' }}>Quick answer</p>
          <p className="text-sm leading-relaxed" style={{ color: 'var(--warm-text)' }}>
            <strong style={{ fontWeight: 600 }}>Per-seat cost = agents × price per agent. Kora cost = tickets × $0.10.</strong> With
            10 agents at $50 each, per-seat is $500/month; at 300 tickets Kora is $30/month. The models
            break even at 5,000 tickets per month for that team.
          </p>
        </div>
      </div>
      <SeatsVsTicketsCalculator variant="full" />
      <p className="text-xs" style={{ color: 'var(--warm-muted)' }}>Nothing is sent anywhere; the math runs in your browser.</p>
    </div>
  );
}

export function HelpdeskCostCalculatorPage() {
  useEffect(() => {
    const title = 'Helpdesk Cost Calculator: Per-Seat vs Per-Ticket';
    const description = 'Helpdesk cost calculator: enter agents, your per-seat price and monthly tickets to compare with Kora at $0.10 per ticket and find your break-even point.';
    setDocumentMeta(title, description);
    setCanonical('/helpdesk-cost-calculator');
    setOgImage('/assets/og-pricing.png');

    const cleanup = injectJsonLdList([
      buildWebPageSchema('/helpdesk-cost-calculator', title, description),
      buildBreadcrumbSchema([
        { name: 'Home', url: 'https://usekora.app/' },
        { name: 'Pricing', url: 'https://usekora.app/pricing' },
        { name: 'Helpdesk Cost Calculator', url: 'https://usekora.app/helpdesk-cost-calculator' },
      ]),
      buildFaqPageSchema(FAQS),
    ]);
    return cleanup;
  }, []);

  return (
    <MarketingShell>
      <PageHero
        eyebrow="Calculator"
        title={<>Helpdesk cost calculator: <br /><Muted><span className="italic">per-seat vs pay-per-ticket</span></Muted></>}
        lead="Enter your team size, the per-agent price you pay today, and your monthly ticket volume. See what the same work costs at $0.10 per ticket, and where the two models break even."
      >
        <CalculatorBlock />
      </PageHero>

      <RevealSection style={BAND}>
        <Eyebrow>The formula</Eyebrow>
        <H2>How the <br /><Muted>math works</Muted></H2>
        <ul className="reveal-stagger mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {MATH.map((m) => (
            <li key={m.title} className="rounded-2xl p-5" style={{ background: 'var(--warm-page)', border: '1px solid var(--warm-border)' }}>
              <IconTile icon={m.icon} bg="var(--warm-card)" />
              <p className="mt-4 text-sm font-semibold" style={{ color: 'var(--warm-text)' }}>{m.title}</p>
              <p className="mt-1 text-sm leading-relaxed" style={{ color: 'var(--warm-muted)' }}>{m.body}</p>
            </li>
          ))}
        </ul>
      </RevealSection>

      <RevealSection innerClassName="grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5">
          <Eyebrow>Be honest</Eyebrow>
          <H2>When per-seat <br /><Muted>still wins</Muted></H2>
        </div>
        <p className="lg:col-span-7 text-lg leading-relaxed" style={{ color: 'var(--warm-muted)' }}>
          If every agent works hundreds of tickets a month, a flat seat price spreads over a lot of work and
          can be cheaper than paying per ticket. Teams with a few agents, lots of collaborators, spiky
          volume or internal requests usually land on the pay-per-ticket side. Run your own numbers above
          instead of trusting a rule of thumb. Also weigh features you need that Kora does not have yet,
          such as email-to-ticket and a live chat widget.
        </p>
      </RevealSection>

      <RevealSection>
        <p className="text-sm" style={{ color: 'var(--warm-muted)' }}>
          Related: <Link to="/helpdesk-without-per-seat-pricing" className="underline" style={{ color: 'var(--warm-text)' }}>helpdesk without per-seat pricing</Link>.
        </p>
      </RevealSection>

      <RevealSection style={BAND}>
        <Eyebrow>FAQ</Eyebrow>
        <H2>Frequently asked <br /><Muted>questions</Muted></H2>
        <div className="mt-10">
          <FaqAccordion items={FAQS.map((f) => ({ question: f.q, answer: f.a }))} twoCol />
        </div>
        <p className="mt-8 text-sm" style={{ color: 'var(--warm-muted)' }}>
          Related: <Link to="/pricing" className="underline" style={{ color: 'var(--warm-text)' }}>Kora pricing</Link>,{' '}
          <Link to="/pay-as-you-go-helpdesk" className="underline" style={{ color: 'var(--warm-text)' }}>pay-as-you-go help desk guide</Link>,{' '}
          <Link to="/freshdesk-alternative" className="underline" style={{ color: 'var(--warm-text)' }}>Freshdesk alternative</Link>.
        </p>
      </RevealSection>

      <CtaBand
        title={<>Try the pay-per-ticket <br /><Muted>help desk</Muted></>}
        lead="1,100 free credits on signup. No card, no seats, no monthly bill."
      />
    </MarketingShell>
  );
}
